import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL, IMAGES } from "@/lib/constants";
import { GoogleButton } from "@/components/GoogleButton";
import { WalletLoginButton } from "@/components/wallet/WalletLoginButton";

export default function Login() {
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setNeedsVerify(false); setResent(false); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/");
    } catch (ex) {
      if (ex.response?.status === 403) setNeedsVerify(true);
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try { await resendVerification(email); setResent(true); } catch (e) {}
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative grain">
        <img src={IMAGES.neon} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        <div className="relative h-full flex items-end p-12">
          <div>
            <h2 className="font-display text-4xl font-black leading-tight">Inside the room where serious market conversations happen.</h2>
            <p className="text-zinc-400 mt-3 font-mono-cl text-xs tracking-[0.2em] uppercase">Signal &gt; Noise</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <img src={LOGO_URL} alt="OnchainLyfe" className="h-8 mb-8" />
          <h1 className="font-display text-3xl font-bold">Welcome back</h1>
          <p className="text-zinc-400 text-sm mt-1 mb-6">Log in to your intelligence layer.</p>
          {err && <div data-testid="login-error" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">{err}</div>}
          {needsVerify && (
            <div className="mb-4 text-sm text-zinc-400">
              {resent
                ? <span data-testid="login-resent-note" className="text-[#ccff00]">Confirmation link sent. Check your inbox.</span>
                : <button type="button" data-testid="login-resend-verification" onClick={resend} className="text-[#ccff00] hover:underline">Resend confirmation email</button>}
            </div>
          )}
          <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="you@fund.com" />
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500">Password</label>
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-zinc-500 hover:text-[#ccff00]">Forgot?</Link>
          </div>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-6 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="••••••••" />
          <button data-testid="login-submit" disabled={busy} className="w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter] disabled:opacity-60">
            {busy ? "Logging in…" : "Log in"}
          </button>
          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] font-mono-cl tracking-[0.2em] uppercase text-zinc-600">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <GoogleButton />
          <WalletLoginButton />
          <p className="text-center text-sm text-zinc-500 mt-6">
            No account? <Link to="/register" className="text-[#ccff00] hover:underline">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
